import { useCallback, useMemo } from "react";
import { useDocumentCurrency } from "@/ProposalBuilder/contexts/DocumentCurrencyContext";
import { useCurrencyConfig } from "@/Hooks/useCurrencyConfig";
import { currencyFormatter, getCurrencySettings } from "@/Utils/Currency";

export interface UseDocumentDisplayCurrencyResult {
	quoteCurrency: string;
	companyCurrency: string;
	isFrozen: boolean;
	isLegacy: boolean;
	isQuoteView: boolean;
	displayCurrency: string;
	oppositeCurrency: string;
	effectiveRate: number;
	frozenRate: number | null;
	liveRate: number;
	frozenAt: string | null;
	liveRateUpdatedAt: string | null;
	isRateStale: boolean;
	effectiveAmount: (quoteAmount: number) => number;
	oppositeAmount: (quoteAmount: number) => number;
	formatAmount: (
		quoteAmount: number | string | null | undefined,
		fallbackValue?: string,
		currencyOptions?: Intl.NumberFormatOptions
	) => string;
	toggle: () => void;
	setQuoteView: (next: boolean) => void;
}

export const useDocumentDisplayCurrency =
	(): UseDocumentDisplayCurrencyResult => {
		const docCtx = useDocumentCurrency();
		const skipLiveConfigFetch =
			(docCtx?.skipLiveConfigFetch ?? false) ||
			((docCtx?.isLocked ?? false) && !!docCtx?.companyCurrency);
		const { config } = useCurrencyConfig({ skip: skipLiveConfigFetch });

		const isQuoteView = docCtx?.isQuoteView ?? false;
		const setQuoteView = docCtx?.setQuoteView ?? (() => {});

		const companyCurrency =
			docCtx?.companyCurrency ??
			config?.primaryCurrency.code ??
			getCurrencySettings().currency;
		const quoteCurrency = docCtx?.currency ?? companyCurrency;
		const isLegacy = !docCtx || docCtx.currency === null;
		const isFrozen = docCtx?.isFrozen ?? false;

		const liveSecondary = useMemo(
			() =>
				isLegacy
					? null
					: config?.currencyConfigs.find(
							c => c.currency.code === quoteCurrency
						) ?? null,
			[config, quoteCurrency, isLegacy]
		);

		const liveRate = liveSecondary?.exchangeRate ?? 1;
		const liveRateUpdatedAt = liveSecondary?.updatedAt ?? null;

		const frozenRate = docCtx?.frozenRate ?? null;
		const frozenAt = docCtx?.frozenAt ?? null;

		const effectiveRate = frozenRate ?? liveRate;

		const hasResolvedLiveRate =
			isLegacy || (docCtx?.isLocked ?? false) || !!config;

		const isRateStale =
			hasResolvedLiveRate &&
			!(docCtx?.isLocked ?? false) &&
			!isLegacy &&
			frozenRate != null &&
			frozenRate !== liveRate;

		const displayCurrency = isQuoteView ? quoteCurrency : companyCurrency;
		const oppositeCurrency = isQuoteView ? companyCurrency : quoteCurrency;

		const effectiveAmount = useCallback(
			(quoteAmount: number) =>
				isQuoteView ? quoteAmount : quoteAmount * effectiveRate,
			[isQuoteView, effectiveRate]
		);

		const oppositeAmount = useCallback(
			(quoteAmount: number) =>
				isQuoteView ? quoteAmount * effectiveRate : quoteAmount,
			[isQuoteView, effectiveRate]
		);

		const formatAmount = useCallback(
			(
				quoteAmount: number | string | null | undefined,
				fallbackValue = "",
				currencyOptions?: Intl.NumberFormatOptions
			) =>
				currencyFormatter(quoteAmount, fallbackValue, currencyOptions, {
					currency: quoteCurrency
				}),
			[quoteCurrency]
		);

		const toggle = useCallback(
			() => setQuoteView(!isQuoteView),
			[isQuoteView, setQuoteView]
		);

		return {
			quoteCurrency,
			companyCurrency,
			isFrozen,
			isLegacy,
			isQuoteView,
			displayCurrency,
			oppositeCurrency,
			effectiveRate,
			frozenRate,
			liveRate,
			frozenAt,
			liveRateUpdatedAt,
			isRateStale,
			effectiveAmount,
			oppositeAmount,
			formatAmount,
			toggle,
			setQuoteView
		};
	};
